<script setup lang="ts">
import { FileText, GripVertical, X } from 'lucide-vue-next';
import { useTranslations } from '@/composables/useTranslations';

type Props = {
    file: File;
    showDragHandle?: boolean;
};

withDefaults(defineProps<Props>(), {
    showDragHandle: false,
});

const emit = defineEmits<{
    (e: 'remove'): void;
}>();

const { t } = useTranslations();

function formatSize(bytes: number): string {
    if (bytes < 1024) {
        return bytes + ' B';
    }

    if (bytes < 1024 * 1024) {
        return (bytes / 1024).toFixed(1) + ' KB';
    }

    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}
</script>

<template>
    <div
        class="flex touch-manipulation items-center gap-2 rounded-lg border border-border bg-card px-2 py-2 shadow-sm sm:gap-3 sm:px-3"
    >
        <GripVertical
            v-if="showDragHandle"
            class="drag-handle size-5 shrink-0 cursor-grab text-gray-400 sm:h-4 sm:w-4"
        />
        <FileText class="size-5 shrink-0 text-brand-600 sm:h-5 sm:w-5" />
        <div class="min-w-0 flex-1">
            <span
                class="block truncate text-sm font-medium text-gray-900 dark:text-gray-100"
            >
                {{ file.name }}
            </span>
            <span
                class="mt-0.5 block text-xs text-gray-500 sm:mt-0 sm:hidden dark:text-gray-400"
            >
                {{ formatSize(file.size) }}
            </span>
        </div>
        <span
            class="hidden shrink-0 text-xs text-gray-500 sm:block dark:text-gray-400"
        >
            {{ formatSize(file.size) }}
        </span>
        <button
            type="button"
            class="-m-1 flex size-10 shrink-0 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-red-600 focus:ring-2 focus:ring-red-500 focus:outline-none active:bg-gray-100 sm:-m-0 sm:size-9 dark:hover:bg-gray-700 dark:hover:text-red-400"
            :aria-label="t('tools.file_card.remove_aria', 'Remove file')"
            @click.stop="emit('remove')"
        >
            <X class="size-5 sm:h-4 sm:w-4" />
        </button>
    </div>
</template>
